import { useState, useMemo, useRef, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import { styled } from '@mui/material/styles';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { DEFAULT_FONT_SIZES, DEFAULT_SIZE } from 'components/editor/grid/reduxStore/defaultTextDefaultValue';
import type { Theme } from '@mui/material/styles';

const extractNumericValue = (fontSize: string | undefined): string => {
  if (!fontSize) return DEFAULT_SIZE;
  const match = fontSize.match(/^(\d+)/);
  return match ? match[1] : DEFAULT_SIZE;
};

const customSelectStyle = {
  fontSize: '12px',
  fontStyle: 'normal',
  color: 'rgba(0, 40, 58, 0.60)',
  fontWeight: 600,
  lineHeight: '166%',
  letterSpacing: '0.45px',
  '& > div:focus': {
    backgroundColor: 'transparent',
  },
};

const FontSizeContainer = styled('div')<{ variant: 'standard' | 'outlined'; isOpen: boolean }>(
  ({ variant, isOpen, theme }: { variant: 'standard' | 'outlined'; isOpen: boolean; theme: Theme }) => ({
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    width: '100%',
    ...(variant === 'standard'
      ? {
          borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
        }
      : {
          border: `1px solid ${isOpen ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.23)'}`,
          borderRadius: 8,
          padding: '6px 8px 6px 14px',
          minHeight: 40,
        }),
  })
);

const FontSizeInputField = styled('input')({
  ...customSelectStyle,
  border: 'none',
  background: 'transparent',
  outline: 'none',
  width: '100%',
  padding: 0,
  margin: 0,
  flex: 1,
  '&:focus': {
    backgroundColor: 'transparent',
  },
});

const DropdownIcon = styled(ArrowDropDownIcon)<{ isOpen: boolean }>(({ isOpen }) => ({
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  color: 'rgba(0, 40, 58, 0.60)',
  transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
}));

type FontSizeInputProps = {
  width: string;
  value: string;
  onChange: (value: string) => void;
  menuItems: string[];
  variant?: 'standard' | 'outlined';
};

const FontSizeInput = ({ width, value, onChange, variant = 'standard' }: FontSizeInputProps) => {
  const [inputValue, setInputValue] = useState(extractNumericValue(value));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const currentNumericValue = useMemo(() => extractNumericValue(value), [value]);

  useEffect(() => {
    setInputValue(currentNumericValue);
  }, [currentNumericValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/px/gi, '');
    if (!val.includes('-')) {
      setInputValue(val);
    }
  };

  const handleChangeFontSize = () => {
    const val = inputValue.trim().replace(/px/gi, '');
    const numericValue = val.match(/^\d+$/);

    if (!numericValue) {
      setInputValue(currentNumericValue);
      return;
    }

    let size = parseInt(val, 10);
    if (size < 1) size = 1;
    if (size > 400) size = 400;

    onChange(`${size}px`);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleChangeFontSize();
    }
  };

  const handleSelectFontSize = (name: string, fontSizeValue: string) => {
    onChange(fontSizeValue);
    setAnchorEl(null);
  };

  const handleDropdownClick = (event: React.MouseEvent<HTMLElement>) => {
    if (event.target !== inputRef.current && !inputRef.current?.contains(event.target as Node)) {
      setAnchorEl(event.currentTarget);
    }
  };

  return (
    <Grid item flexGrow={1} width={width} maxWidth={width}>
      <FormControl variant={variant} size="small" fullWidth>
        <FontSizeContainer onClick={handleDropdownClick} variant={variant} isOpen={Boolean(anchorEl)}>
          <FontSizeInputField
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onBlur={handleChangeFontSize}
            onClick={(e) => e.stopPropagation()}
          />
          <DropdownIcon isOpen={Boolean(anchorEl)} />
        </FontSizeContainer>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          MenuListProps={{
            'aria-labelledby': 'font-size-menu',
          }}
          disableScrollLock
        >
          {DEFAULT_FONT_SIZES.map(({ value: fontSizeValue, name }) => (
            <MenuItem
              key={fontSizeValue}
              onClick={() => handleSelectFontSize(name, fontSizeValue)}
              selected={fontSizeValue === value}
              sx={{ minWidth: '50px', justifyContent: 'center' }}
            >
              {name}
            </MenuItem>
          ))}
        </Menu>
      </FormControl>
    </Grid>
  );
};

export default FontSizeInput;
